/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Plus, Loader2, Trash2, Shield, Building2, Crown, Pause, Play, Edit, Landmark, BarChart3, DollarSign, MessageSquare, Users, Wallet, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
// v3.15-1136A — guards the Edit modal so one malformed company doc can't
// nuke the whole Companies page.
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';

const CompaniesPage = () => {
  const navigate = useNavigate();
  const { isAdmin, isInternal } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [financials, setFinancials] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '' });
  const [editCompany, setEditCompany] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await api.get('/companies');
      const sorted = response.data.sort((a, b) => {
        const orderA = a.sort_order ?? 999;
        const orderB = b.sort_order ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      });
      setCompanies(sorted);

      // Fetch financials for each external company
      const finMap = {};
      await Promise.all(sorted.filter(c => c.company_type !== 'internal').map(async (c) => {
        try {
          const res = await api.get(`/companies/${c.id}/financial-summary`);
          finMap[c.id] = res.data;
        } catch { /* skip */ }
      }));
      setFinancials(finMap);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await api.post('/companies', { name: newCompany.name, is_admin_company: false });
      toast.success('Company created successfully');
      setIsCreateOpen(false);
      setNewCompany({ name: '' });
      fetchCompanies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create company'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete company'));
    }
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    setUpdatingStatus(companyId);
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await api.put(`/companies/${companyId}/status?status=${newStatus}`);
      toast.success(`Company ${newStatus === 'active' ? 'reactivated' : 'paused'}`);
      fetchCompanies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update company status'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleMoveCompany = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= companies.length) return;

    const updated = [...companies];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];

    // Optimistic update
    setCompanies(updated);

    // Save sort_order for both swapped companies
    try {
      await Promise.all([
        api.put(`/companies/${updated[index].id}`, { sort_order: index }),
        api.put(`/companies/${updated[targetIndex].id}`, { sort_order: targetIndex }),
      ]);
    } catch (error) {
      toast.error('Failed to reorder');
      fetchCompanies();
    }
  };

  const getStatusBadge = (status, companyType) => {
    if (companyType === 'internal') return null;
    
    switch (status) {
      case 'paused':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Paused</Badge>;
      case 'suspended':
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Suspended</Badge>;
      default:
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Active</Badge>;
    }
  };

  const handleEditCompany = (company) => {
    setEditCompany({
      id: company.id,
      name: company.name,
      status: company.status || 'active',
      company_type: company.company_type,
      industry_category: company.industry_category || '',
      plan_type: company.plan_type || 'Standard',
      contact_phone: company.contact_phone || '',
      contact_email: company.contact_email || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      bank_name: company.bank_name || '',
      bank_routing_number: company.bank_routing_number || '',
      bank_account_number: company.bank_account_number || '',
      bank_address: company.bank_address || '',
      lending_enabled: company.lending_enabled || false,
      advanced_reporting_enabled: company.advanced_reporting_enabled || false,
      settlement_access_enabled: company.settlement_access_enabled || false,
      sender_email: company.email_sender?.sender_email || '',
      sender_name: company.email_sender?.sender_name || '',
      // v3.15-1125 — Partner Delivery configuration
      partner_delivery_method: company.partner_delivery_config?.method || '',
      partner_api_endpoint: company.partner_delivery_config?.api?.endpoint || '',
      partner_api_auth_type: company.partner_delivery_config?.api?.auth_type || 'none',
      partner_api_username: company.partner_delivery_config?.api?.username || '',
      partner_api_password: company.partner_delivery_config?.api?.password || '',
      partner_api_bearer_token: company.partner_delivery_config?.api?.bearer_token || '',
      partner_api_additional_headers_json: company.partner_delivery_config?.api?.additional_headers
        ? JSON.stringify(company.partner_delivery_config.api.additional_headers, null, 2) : '',
      partner_email_recipients: (company.partner_delivery_config?.email?.recipients || []).join(', '),
      partner_email_cc_recipients: (company.partner_delivery_config?.email?.cc_recipients || []).join(', '),
      partner_email_subject_template: company.partner_delivery_config?.email?.subject_template || '',
      partner_email_body_template: company.partner_delivery_config?.email?.body_template || '',
      partner_manual_instructions: company.partner_delivery_config?.manual?.instructions || '',
    });
    setIsEditOpen(true);
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!editCompany) return;

    setSaving(true);
    try {
      // v3.15-1125 — build partner_delivery_config from the method-specific fields
      let partner_delivery_config = null;
      const method = (editCompany.partner_delivery_method || '').toLowerCase();
      if (method === 'api') {
        let addlHeaders = null;
        const raw = (editCompany.partner_api_additional_headers_json || '').trim();
        if (raw) {
          try { addlHeaders = JSON.parse(raw); } catch { addlHeaders = null; }
        }
        partner_delivery_config = {
          method: 'api',
          api: {
            endpoint: (editCompany.partner_api_endpoint || '').trim(),
            auth_type: editCompany.partner_api_auth_type || 'none',
            username: editCompany.partner_api_username || '',
            password: editCompany.partner_api_password || '',
            bearer_token: editCompany.partner_api_bearer_token || '',
            additional_headers: addlHeaders || {},
          },
        };
      } else if (method === 'email') {
        const parseList = (s) => (s || '').split(/[,\n]/).map(x => x.trim()).filter(Boolean);
        partner_delivery_config = {
          method: 'email',
          email: {
            recipients: parseList(editCompany.partner_email_recipients),
            cc_recipients: parseList(editCompany.partner_email_cc_recipients),
            subject_template: editCompany.partner_email_subject_template || '',
            body_template: editCompany.partner_email_body_template || '',
          },
        };
      } else if (method === 'manual') {
        partner_delivery_config = {
          method: 'manual',
          manual: { instructions: editCompany.partner_manual_instructions || '' },
        };
      }

      await api.put(`/companies/${editCompany.id}`, {
        name: editCompany.name,
        status: editCompany.status,
        industry_category: editCompany.industry_category || null,
        plan_type: editCompany.plan_type,
        contact_phone: editCompany.contact_phone,
        contact_email: editCompany.contact_email,
        address: editCompany.address,
        city: editCompany.city,
        state: editCompany.state,
        zip_code: editCompany.zip_code,
        bank_name: editCompany.bank_name,
        bank_routing_number: editCompany.bank_routing_number,
        bank_account_number: editCompany.bank_account_number,
        bank_address: editCompany.bank_address,
        lending_enabled: editCompany.lending_enabled,
        advanced_reporting_enabled: editCompany.advanced_reporting_enabled,
        settlement_access_enabled: editCompany.settlement_access_enabled,
        email_sender: {
          sender_email: editCompany.sender_email || '',
          sender_name: editCompany.sender_name || '',
        },
        ...(partner_delivery_config !== null ? { partner_delivery_config } : {}),
      });
      toast.success('Company updated successfully');
      setIsEditOpen(false);
      setEditCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update company'));
    } finally {
      setSaving(false);
    }
  };

  const getPlanBadge = (planType) => {
    switch (planType) {
      case 'Funding Partner':
        return <span className="text-xs font-medium text-accent">Plan: Funding Partner</span>;
      case 'Enterprise':
        return <span className="text-xs font-medium text-purple-600">Plan: Enterprise</span>;
      default:
        return <span className="text-xs text-slate-500">Plan: Standard Partner</span>;
    }
  };

  if (!isInternal() || !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">You don't have permission to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Companies</h1>
          <p className="text-sm text-slate-500 mt-1">Manage partner companies</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-gold-dark" data-testid="create-company-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Company</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  required
                  placeholder="Enter company name"
                  data-testid="company-name-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-accent hover:bg-gold-dark" disabled={creating} data-testid="submit-company-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Company'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : companies.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No companies found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company, idx) => (
            <Card key={company.id} className="card-hover" data-testid={`company-card-${company.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {/* Reorder arrows */}
                    <div className="flex flex-col gap-0.5 -ml-1">
                      <button
                        onClick={() => handleMoveCompany(idx, 'up')}
                        disabled={idx === 0}
                        className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-20 disabled:cursor-not-allowed"
                        data-testid={`move-up-${company.id}`}
                      >
                        <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                      <button
                        onClick={() => handleMoveCompany(idx, 'down')}
                        disabled={idx === companies.length - 1}
                        className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-20 disabled:cursor-not-allowed"
                        data-testid={`move-down-${company.id}`}
                      >
                        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                    </div>
                    <div className={`p-3 rounded-xl ${company.company_type === 'internal' ? 'bg-accent/10' : 'bg-slate-100'}`}>
                      {company.company_type === 'internal' ? (
                        <Crown className="h-6 w-6 text-accent" />
                      ) : (
                        <Building2 className="h-6 w-6 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{company.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {company.company_type === 'internal' && (
                          <span className="text-xs text-accent font-medium">PTC (Internal)</span>
                        )}
                        {getStatusBadge(company.status, company.company_type)}
                      </div>
                      {/* Plan Type for External Companies */}
                      {company.company_type !== 'internal' && (
                        <div className="mt-1 flex items-center gap-2">
                          {getPlanBadge(company.plan_type)}
                          {company.industry_category && (
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {company.industry_category.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Feature Indicators for External Companies */}
                {company.company_type !== 'internal' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {company.lending_enabled && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        <Landmark className="h-3 w-3 mr-1" />
                        Lending
                      </Badge>
                    )}
                    {company.settlement_access_enabled && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Settlement
                      </Badge>
                    )}
                    {company.advanced_reporting_enabled && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                        <BarChart3 className="h-3 w-3 mr-1" />
                        Advanced Reports
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Inline Financial Summary */}
                {company.company_type !== 'internal' && financials[company.id] && (
                  <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-1.5 text-xs">
                    <div>
                      <span className="text-slate-400">Volume</span>
                      <p className="font-semibold text-slate-700">${(financials[company.id].total_deal_volume || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Deals / Funded</span>
                      <p className="font-semibold text-slate-700">{financials[company.id].total_deal_count || 0} / {financials[company.id].funded_count || 0}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">We Owe</span>
                      <p className="font-semibold text-green-600">${(financials[company.id].amount_we_owe || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">They Owe</span>
                      <p className="font-semibold text-blue-600">${(financials[company.id].amount_partner_owes || 0).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Contact Info Preview */}
                {company.company_type !== 'internal' && (company.contact_phone || company.bank_name) && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-0.5">
                    {company.contact_phone && <p>{company.contact_phone}</p>}
                    {company.bank_name && <p>Bank: {company.bank_name}</p>}
                  </div>
                )}

                {/* Action Buttons for External Companies */}
                {company.company_type !== 'internal' && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-400">
                      Created {formatET(company.created_at, 'MMM d, yyyy')}
                    </p>
                    <div className="flex items-center gap-2">
                      {/* Financials Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/companies/${company.id}`)}
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`financials-company-${company.id}`}
                      >
                        <Wallet className="h-4 w-4 mr-1" />
                        Financials
                      </Button>
                      
                      {/* Edit Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditCompany(company)}
                        data-testid={`edit-company-${company.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>

                      {/* Pause/Reactivate Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(company.id, company.status || 'active')}
                        disabled={updatingStatus === company.id}
                        className={company.status === 'paused' ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}
                        data-testid={`toggle-status-${company.id}`}
                      >
                        {updatingStatus === company.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : company.status === 'paused' ? (
                          <>
                            <Play className="h-4 w-4 mr-1" />
                            Reactivate
                          </>
                        ) : (
                          <>
                            <Pause className="h-4 w-4 mr-1" />
                            Pause
                          </>
                        )}
                      </Button>
                      
                      {/* Delete Button */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" data-testid={`delete-company-${company.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Company</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {company.name}? This will remove all associated users and clients.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteCompany(company.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                )}
                
                {/* Internal company - just show created date */}
                {company.company_type === 'internal' && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-400">
                      Created {formatET(company.created_at, 'MMM d, yyyy')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Company Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
          </DialogHeader>
          {/* v3.15-1136A — defensive boundary so a single malformed field
              (orphan partner_delivery_config, unknown industry_category,
              future schema mismatch, etc.) shows a friendly recoverable
              error inside the modal instead of crashing the whole page
              into a white screen. */}
          <SectionErrorBoundary label="Edit Company" testId="edit-company-error-boundary">
          <form onSubmit={handleSaveCompany} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Company Name</Label>
              <Input
                id="edit-company-name"
                value={editCompany?.name || ''}
                onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })}
                required
                data-testid="edit-company-name-input"
              />
            </div>
            
            {editCompany?.company_type !== 'internal' && (
              <>
                <div className="space-y-2">
                  <Label>Company Status</Label>
                  <Select
                    value={editCompany?.status || 'active'}
                    onValueChange={(value) => setEditCompany({ ...editCompany, status: value })}
                  >
                    <SelectTrigger data-testid="edit-company-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Partner Plan</Label>
                  <Select
                    value={editCompany?.plan_type || 'Standard'}
                    onValueChange={(value) => setEditCompany({ ...editCompany, plan_type: value })}
                  >
                    <SelectTrigger data-testid="edit-company-plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard Partner</SelectItem>
                      <SelectItem value="Funding Partner">Funding Partner</SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Industry Category</Label>
                  <Select
                    value={editCompany?.industry_category || ''}
                    onValueChange={(value) => setEditCompany({ ...editCompany, industry_category: value })}
                  >
                    <SelectTrigger data-testid="edit-company-industry-select">
                      <SelectValue placeholder="Select industry..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solar_sales">Solar Sales</SelectItem>
                      <SelectItem value="solar_exit">Solar Exit</SelectItem>
                      <SelectItem value="travel_club_sales">Travel Club Sales</SelectItem>
                      <SelectItem value="timeshare_exit">Timeshare Exit</SelectItem>
                      <SelectItem value="automotive">Automotive</SelectItem>
                      <SelectItem value="home_improvement">Home Improvement</SelectItem>
                      <SelectItem value="medical">Medical / Dental</SelectItem>
                      <SelectItem value="debt_consolidation">Debt Consolidation</SelectItem>
                      {/* v3.15-1120: Business Funding industry — triggers BF
                          intake schema instead of standard client schema for
                          any company in this category. */}
                      <SelectItem value="business_funding">Business Funding</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Contact Info */}
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-base font-semibold">Contact Information</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Phone</Label>
                      <Input
                        value={editCompany?.contact_phone || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, contact_phone: e.target.value })}
                        placeholder="(555) 555-5555"
                        data-testid="edit-company-phone"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Email</Label>
                      <Input
                        value={editCompany?.contact_email || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, contact_email: e.target.value })}
                        placeholder="contact@company.com"
                        data-testid="edit-company-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-base font-semibold">Company Address</Label>
                  <div className="space-y-2">
                    <Input
                      value={editCompany?.address || ''}
                      onChange={(e) => setEditCompany({ ...editCompany, address: e.target.value })}
                      placeholder="Street Address"
                      data-testid="edit-company-address"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={editCompany?.city || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, city: e.target.value })}
                        placeholder="City"
                        data-testid="edit-company-city"
                      />
                      <Input
                        value={editCompany?.state || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, state: e.target.value })}
                        placeholder="State"
                        data-testid="edit-company-state"
                      />
                      <Input
                        value={editCompany?.zip_code || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, zip_code: e.target.value })}
                        placeholder="Zip"
                        data-testid="edit-company-zip"
                      />
                    </div>
                  </div>
                </div>

                {/* Wire / Bank Info */}
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-base font-semibold">Wire / Bank Information</Label>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Bank Name</Label>
                      <Input
                        value={editCompany?.bank_name || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, bank_name: e.target.value })}
                        placeholder="Chase Bank"
                        data-testid="edit-company-bank-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Routing Number</Label>
                        <Input
                          value={editCompany?.bank_routing_number || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, bank_routing_number: e.target.value })}
                          placeholder="021000021"
                          data-testid="edit-company-routing"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Account Number</Label>
                        <Input
                          value={editCompany?.bank_account_number || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, bank_account_number: e.target.value })}
                          placeholder="123456789"
                          data-testid="edit-company-account"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Bank Address</Label>
                      <Input
                        value={editCompany?.bank_address || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, bank_address: e.target.value })}
                        placeholder="123 Bank St, City, State ZIP"
                        data-testid="edit-company-bank-address"
                      />
                    </div>
                  </div>
                </div>

                {/* Email Settings */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <Label className="text-base font-semibold">Email Settings</Label>
                    <p className="text-xs text-slate-500 mt-1">Control the sender identity for all emails sent from this company. If empty, system default (support@easyquickfund.com) is used.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Sender Email</Label>
                      <Input
                        value={editCompany?.sender_email || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, sender_email: e.target.value })}
                        placeholder="support@easyquickfund.com"
                        data-testid="edit-company-sender-email"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Sender Name</Label>
                      <Input
                        value={editCompany?.sender_name || ''}
                        onChange={(e) => setEditCompany({ ...editCompany, sender_name: e.target.value })}
                        placeholder="EasyQuickFund Support"
                        data-testid="edit-company-sender-name"
                      />
                    </div>
                  </div>
                </div>

                {/* v3.15-1125 — Partner Delivery configuration */}
                <div className="space-y-3 pt-4 border-t" data-testid="edit-company-partner-delivery">
                  <div>
                    <Label className="text-base font-semibold">Partner Delivery</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      How application data is delivered to this partner when a user clicks <span className="font-medium">Send To Partner</span>.
                      Configure once per company; the Send button reads this and executes automatically.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Delivery Method</Label>
                    {/* v3.15-1136A — Radix UI forbids empty-string SelectItem values
                        (it throws "A <Select.Item /> must have a value prop that is
                        not an empty string", crashing the whole page when the modal
                        opens for a company whose `partner_delivery_method` is unset).
                        We use the sentinel "unset" both in the value and onChange,
                        and map it back to '' for the save payload. */}
                    <Select
                      value={editCompany?.partner_delivery_method || 'unset'}
                      onValueChange={(v) => setEditCompany({ ...editCompany, partner_delivery_method: v === 'unset' ? '' : v })}
                    >
                      <SelectTrigger data-testid="edit-company-partner-method">
                        <SelectValue placeholder="Select delivery method..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">— Not configured —</SelectItem>
                        <SelectItem value="api">API (HTTP POST)</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="manual">Manual (operator delivers off-platform)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* API method fields */}
                  {editCompany?.partner_delivery_method === 'api' && (
                    <div className="space-y-2 pl-3 border-l-2 border-blue-200" data-testid="edit-company-partner-api-fields">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">API Endpoint</Label>
                        <Input
                          value={editCompany?.partner_api_endpoint || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_api_endpoint: e.target.value })}
                          placeholder="https://api.partner.com/webhook/applications"
                          data-testid="edit-company-partner-api-endpoint"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Auth Type</Label>
                          <Select
                            value={editCompany?.partner_api_auth_type || 'none'}
                            onValueChange={(v) => setEditCompany({ ...editCompany, partner_api_auth_type: v })}
                          >
                            <SelectTrigger data-testid="edit-company-partner-api-auth-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="basic">Basic Auth</SelectItem>
                              <SelectItem value="bearer">Bearer Token</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {editCompany?.partner_api_auth_type === 'basic' && (
                          <>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Username</Label>
                              <Input
                                value={editCompany?.partner_api_username || ''}
                                onChange={(e) => setEditCompany({ ...editCompany, partner_api_username: e.target.value })}
                                data-testid="edit-company-partner-api-username"
                              />
                            </div>
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs text-slate-500">Password</Label>
                              <Input
                                type="password"
                                value={editCompany?.partner_api_password || ''}
                                onChange={(e) => setEditCompany({ ...editCompany, partner_api_password: e.target.value })}
                                data-testid="edit-company-partner-api-password"
                              />
                            </div>
                          </>
                        )}
                        {editCompany?.partner_api_auth_type === 'bearer' && (
                          <div className="space-y-1 col-span-2">
                            <Label className="text-xs text-slate-500">Bearer Token</Label>
                            <Input
                              type="password"
                              value={editCompany?.partner_api_bearer_token || ''}
                              onChange={(e) => setEditCompany({ ...editCompany, partner_api_bearer_token: e.target.value })}
                              data-testid="edit-company-partner-api-bearer"
                            />
                          </div>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Additional Headers (JSON)</Label>
                        <Textarea
                          value={editCompany?.partner_api_additional_headers_json || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_api_additional_headers_json: e.target.value })}
                          placeholder='{"X-Hub-Source": "PTC-Hub"}'
                          rows={2}
                          className="font-mono text-xs"
                          data-testid="edit-company-partner-api-headers"
                        />
                      </div>
                    </div>
                  )}

                  {/* Email method fields */}
                  {editCompany?.partner_delivery_method === 'email' && (
                    <div className="space-y-2 pl-3 border-l-2 border-amber-200" data-testid="edit-company-partner-email-fields">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Recipients (comma-separated)</Label>
                        <Textarea
                          value={editCompany?.partner_email_recipients || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_email_recipients: e.target.value })}
                          placeholder="partner@example.com, ops@example.com"
                          rows={2}
                          className="text-xs"
                          data-testid="edit-company-partner-email-recipients"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">CC Recipients (optional)</Label>
                        <Textarea
                          value={editCompany?.partner_email_cc_recipients || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_email_cc_recipients: e.target.value })}
                          placeholder="cc1@example.com, cc2@example.com"
                          rows={2}
                          className="text-xs"
                          data-testid="edit-company-partner-email-cc"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Subject Template</Label>
                        <Input
                          value={editCompany?.partner_email_subject_template || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_email_subject_template: e.target.value })}
                          placeholder="New application — {{client_name}}"
                          data-testid="edit-company-partner-email-subject"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Body Template (HTML)</Label>
                        <Textarea
                          value={editCompany?.partner_email_body_template || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_email_body_template: e.target.value })}
                          placeholder='<p>New application from {{client_name}}</p>'
                          rows={4}
                          className="font-mono text-xs"
                          data-testid="edit-company-partner-email-body"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 italic">
                        Supports <code className="bg-slate-100 px-1 rounded">{'{{client_name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{business_name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{application_id}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{amount_requested}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{deal_amount}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{company_name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{sender}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{payload_json}}'}</code>.
                      </p>
                    </div>
                  )}

                  {/* Manual method fields */}
                  {editCompany?.partner_delivery_method === 'manual' && (
                    <div className="space-y-2 pl-3 border-l-2 border-slate-300" data-testid="edit-company-partner-manual-fields">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Submission Instructions</Label>
                        <Textarea
                          value={editCompany?.partner_manual_instructions || ''}
                          onChange={(e) => setEditCompany({ ...editCompany, partner_manual_instructions: e.target.value })}
                          placeholder="Forward the application PDF to partners@example.com with the subject 'New PTC application'."
                          rows={3}
                          className="text-xs"
                          data-testid="edit-company-partner-manual-instructions"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 italic">
                        Send-To-Partner will record the submission but no automated delivery is performed. The operator is responsible for forwarding the application per the instructions above.
                      </p>
                    </div>
                  )}
                </div>

                {/* Company Features Section */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label className="text-base font-semibold">Company Features</Label>
                    <p className="text-sm text-slate-500 mt-1">Enable or disable specific features for this company</p>
                  </div>
                  
                  <div className="space-y-3">
                    {/* CRM Access - Always enabled */}
                    <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50">
                      <Checkbox id="crm-access" checked disabled />
                      <div className="flex-1">
                        <label htmlFor="crm-access" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-500" />
                          CRM Access
                        </label>
                        <p className="text-xs text-slate-500">Client management and pipeline (always enabled)</p>
                      </div>
                    </div>

                    {/* Communications - Always enabled */}
                    <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50">
                      <Checkbox id="communications" checked disabled />
                      <div className="flex-1">
                        <label htmlFor="communications" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-slate-500" />
                          Communications
                        </label>
                        <p className="text-xs text-slate-500">Internal messaging and announcements (always enabled)</p>
                      </div>
                    </div>

                    {/* Lending Marketplace */}
                    <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <Checkbox
                        id="lending-enabled"
                        checked={editCompany?.lending_enabled || false}
                        onCheckedChange={(checked) => setEditCompany({ ...editCompany, lending_enabled: checked })}
                        data-testid="edit-company-lending-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="lending-enabled" className="text-sm font-medium text-slate-700 flex items-center gap-2 cursor-pointer">
                          <Landmark className="h-4 w-4 text-accent" />
                          Lending Marketplace
                        </label>
                        <p className="text-xs text-slate-500">Access to EasyQuickFund lending offers</p>
                      </div>
                    </div>

                    {/* Settlement Tracking */}
                    <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <Checkbox
                        id="settlement-enabled"
                        checked={editCompany?.settlement_access_enabled || false}
                        onCheckedChange={(checked) => setEditCompany({ ...editCompany, settlement_access_enabled: checked })}
                        data-testid="edit-company-settlement-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="settlement-enabled" className="text-sm font-medium text-slate-700 flex items-center gap-2 cursor-pointer">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          Settlement Tracking
                        </label>
                        <p className="text-xs text-slate-500">Commission and settlement reports</p>
                      </div>
                    </div>

                    {/* Advanced Reporting */}
                    <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <Checkbox
                        id="reporting-enabled"
                        checked={editCompany?.advanced_reporting_enabled || false}
                        onCheckedChange={(checked) => setEditCompany({ ...editCompany, advanced_reporting_enabled: checked })}
                        data-testid="edit-company-reporting-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="reporting-enabled" className="text-sm font-medium text-slate-700 flex items-center gap-2 cursor-pointer">
                          <BarChart3 className="h-4 w-4 text-purple-600" />
                          Advanced Reporting
                        </label>
                        <p className="text-xs text-slate-500">Detailed analytics and custom reports</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditCompany(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-accent hover:bg-gold-dark"
                data-testid="save-company-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
          </SectionErrorBoundary>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesPage;
